/**
 * The arithmetic behind the pixel art (DESIGN.md §9, "Pixel art"): a hash, a
 * smooth noise and the field of light every scene is drawn from. Pure, so the
 * same scene draws the same picture on every machine.
 */

/** A 4×4 ordered dither, so a ramp's steps break up into a mosaic, not bands. */
export const BAYER: readonly number[] = [
  0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5,
].map((value) => value / 16);

export function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x * 374761393 + y * 668265263 + seed * 982451653, 1274126177);
  h ^= h >>> 13;
  h = Math.imul(h, 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fbm(x: number, y: number, seed: number): number {
  return (
    0.55 * valueNoise(x, y, seed) +
    0.3 * valueNoise(x * 2.1, y * 2.1, seed + 1) +
    0.15 * valueNoise(x * 4.3, y * 4.3, seed + 2)
  );
}

/** One picture: where its light sits and how it is shaped. */
export interface Scene {
  readonly seed: number;
  readonly rot: number;
  readonly twist: number;
  readonly inner: number;
  readonly outer: number;
  readonly rays: number;
  readonly nz: number;
  readonly ox: number;
  readonly oy: number;
  readonly cx: number;
  readonly cy: number;
  readonly zoom: number;
}

/**
 * How bright the field is at a point: noise for the mosaic, arms for the
 * veins, the mark's three lobes, and a bright core.
 */
export function brightness(u: number, v: number, scene: Scene): number {
  const r = Math.hypot(u, v) + 1e-6;
  const th = Math.atan2(v, u) + scene.rot;
  const n = fbm(u * scene.nz + scene.ox, v * scene.nz + scene.oy, scene.seed);
  const n2 = valueNoise(
    u * scene.nz * 2.2 + scene.oy,
    v * scene.nz * 2.2 + scene.ox,
    scene.seed + 5,
  );
  const arms =
    Math.pow(
      Math.abs(Math.cos((scene.rays / 2) * th + scene.twist * r + (n - 0.5) * 1.4)),
      9,
    ) *
    Math.max(0, 1 - r * 0.55) *
    (0.55 + 0.7 * n2);
  const lobe = Math.max(0, Math.cos(3 * th));
  const petal = Math.max(0, 1 - r / (scene.inner + scene.outer * Math.pow(lobe, 0.6)));
  const core = Math.exp(-r * r * 7);
  return 0.3 + 0.62 * (n - 0.5) + 0.5 * arms + 0.2 * petal + 0.42 * core - 0.1 * r * r;
}

export function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * Fast, then settling: how a cell lands when the picture arrives. No
 * overshoot — nothing in Crux bounces (DESIGN.md §8).
 */
export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
